import { useState } from "react";
import { useAuth } from "../../auth/useAuth";
import * as S from "./sheet";

type Mode = "login" | "recover";

export function LoginScreen({ onClose, onSwitchToSignup }: { onClose: () => void; onSwitchToSignup: () => void }) {
  const { login, redeemRecovery } = useAuth();
  const [mode, setMode] = useState<Mode>("login");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === "login") {
        await login({ emailOrUsername: identifier, password });
      } else {
        await redeemRecovery({ emailOrUsername: identifier, recoveryCode, newPassword: password });
      }
      onClose();
    } catch (err) {
      setError((err as { message?: string }).message ?? "Sign-in failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.card} onClick={(e) => e.stopPropagation()}>
        <div style={S.header}>
          <span style={S.title}>{mode === "login" ? "Sign in" : "Use recovery code"}</span>
          <button onClick={onClose} style={S.closeButton} aria-label="Close">
            ×
          </button>
        </div>
        <form onSubmit={submit} style={S.body}>
          <div style={S.field}>
            <label style={S.label}>Email or username</label>
            <input
              style={S.input}
              type="text"
              autoComplete="username"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
              autoFocus
            />
          </div>

          {mode === "recover" && (
            <div style={S.field}>
              <label style={S.label}>Recovery code</label>
              <input
                style={S.input}
                type="text"
                autoComplete="off"
                value={recoveryCode}
                onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX"
                required
              />
            </div>
          )}

          <div style={S.field}>
            <label style={S.label}>{mode === "login" ? "Password" : "New password"}</label>
            <input
              style={S.input}
              type="password"
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={10}
            />
          </div>

          {error && <div style={S.errorText}>{error}</div>}

          <button type="submit" style={{ ...S.primaryButton, opacity: busy ? 0.6 : 1 }} disabled={busy}>
            {busy ? "Working…" : mode === "login" ? "Sign in" : "Set new password"}
          </button>

          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
            <button
              type="button"
              style={S.linkButton}
              onClick={() => {
                setMode(mode === "login" ? "recover" : "login");
                setError(null);
                setPassword("");
              }}
            >
              {mode === "login" ? "Use recovery code" : "Back to sign in"}
            </button>
            <button type="button" style={S.linkButton} onClick={onSwitchToSignup}>
              Create account
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
